/* Luxuntique 2026 — load and scroll animations.
   snippets/lux-motion.liquid puts .lux-motion and data-lux-motion ("full" or "lite") on <html>
   and holds the styles. This module marks elements .is-in as they scroll into view (a few at a
   time, in page order), then .is-done once the motion has finished so their own hover styles
   take over again. Keep REVEAL in step with the selector lists in the snippet. */

const root = document.documentElement;
const mode = root.dataset.luxMotion;

const REVEAL_FULL = [
  '.lux-story .lux-eyebrow:not(.lux-article__intro > *)',
  '.lux-categories .lux-eyebrow',
  '.lux-request .lux-eyebrow',
  '.lux-story-intro__heading',
  '.lux-story-intro__lead',
  '.lux-story-intro__body',
  '.lux-story-intro__pillar',
  '.lux-story-intro__tags',
  '.lux-story-cards__heading',
  '.lux-story-cards__aside',
  '.lux-story-cards__card',
  '.lux-story-values__heading',
  '.lux-story-values__value',
  '.lux-story-values__stat',
  '.lux-story-feature__heading',
  '.lux-story-feature__lead',
  '.lux-story-feature__text',
  '.lux-story-feature__result',
  '.lux-story-feature__footnote',
  '.lux-story-feature__actions',
  '.lux-story-closing__heading',
  '.lux-story-closing__text',
  '.lux-story-closing__quote',
  '.lux-story-closing__actions',
  '.lux-story-closing__legal',
  '.lux-steps__heading',
  '.lux-steps__step',
  '.lux-faq__heading',
  '.lux-faq__text',
  '.lux-faq__item',
  '.lux-promises__item',
  '.lux-categories__heading',
  '.lux-categories__item',
  '.lux-brands__eyebrow',
  '.lux-brands__item',
  '.lux-request__title',
  '.lux-request__text',
  '.lux-request__actions',
  '.lux-request__step',
  '.lux-request__footnote',
  '.lux-reviews__header',
  '.lux-reviews__viewport',
  '.lux-newsletter__panel',
  '.lux-request-form__head',
  '.lux-request-form__panel',
  '.lux-article__fact',
  '.lux-article__results-heading',
  '.lux-article__result',
  '.lux-article__photo',
  '.lux-article__footnote',
  '.section-resource-list__header',
  '.resource-list__item',
  '.lux-story-intro__media',
  '.lux-story-feature__media',
  '.lux-story--divider > .lux-story__inner',
];

const REVEAL_ALWAYS = ['.lux-request-card', '.lux-sg__prompt-btn', '.lux-size-page__finder-card', '.lux-size-switch'];

const selector = (mode === 'full' ? [...REVEAL_FULL, ...REVEAL_ALWAYS] : REVEAL_ALWAYS).join(',');

const STAGGER = 0.09; // seconds between items revealed together
const MAX_STAGGER = 6;
const DONE_AFTER = 2600; // ms, longer than the slowest transition

/** @type {Element[]} */
let queue = [];
let frame = 0;

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      observer.unobserve(entry.target);
      queue.push(entry.target);
    }
    if (queue.length && !frame) frame = requestAnimationFrame(flush);
  },
  { rootMargin: '0px 0px -3% 0px', threshold: 0.01 }
);

function flush() {
  frame = 0;
  const batch = queue.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  queue = [];

  batch.forEach((element, index) => {
    if (!(element instanceof HTMLElement)) return;
    const delay = Math.min(index, MAX_STAGGER) * STAGGER;
    element.style.setProperty('--lux-delay', `${delay}s`);
    if (element.classList.contains('lux-sg__prompt-btn')) setMeasureMark(element);
    element.classList.add('is-in');
    countUp(element, delay);
    setTimeout(() => {
      element.classList.add('is-done');
      element.style.removeProperty('--lux-delay');
    }, DONE_AFTER + delay * 1000);
  });
}

/** Where the Find my size marker settles: about two thirds along the tape. @param {HTMLElement} button */
function setMeasureMark(button) {
  const tape = button.clientWidth - 32;
  if (tape > 0) button.style.setProperty('--lux-sg-mark', `${Math.round(tape * 0.64)}px`);
}

/** Counts whole numbers up ("130+" counts to 130, then shows the +). Ranges like "2–3 wks" stay as they are.
 * @param {HTMLElement} scope @param {number} delay */
function countUp(scope, delay) {
  for (const number of scope.querySelectorAll('.lux-story-values__number, .lux-story-feature__stat-number')) {
    if (!(number instanceof HTMLElement) || number.dataset.luxCounted) continue;
    number.dataset.luxCounted = 'true';
    const match = number.textContent.trim().match(/^(\d{1,5})(\s*[+%]?[^\d–-]*)$/);
    if (!match) continue;
    const target = Number(match[1]);
    const suffix = match[2];
    if (target < 5) continue;

    const finalText = number.textContent;
    number.setAttribute('aria-label', finalText.trim());
    const duration = 1400;
    const start = performance.now() + delay * 1000 + 200;
    number.textContent = `0${suffix}`;

    const tick = (/** @type {number} */ now) => {
      const progress = Math.min(Math.max((now - start) / duration, 0), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      number.textContent = `${Math.round(target * eased)}${suffix}`;
      if (progress < 1) requestAnimationFrame(tick);
      else number.textContent = finalText;
    };
    requestAnimationFrame(tick);
  }
}

/** @param {ParentNode} scope */
function scan(scope) {
  for (const element of scope.querySelectorAll(selector)) {
    if (!element.classList.contains('is-in')) observer.observe(element);
  }
}

scan(document);
window.__luxMotionReady = true;

// New markup: theme editor section reloads, more products loaded into a grid, Quick Add
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) scan(event.target);
});

new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(selector) && !node.classList.contains('is-in')) observer.observe(node);
      scan(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });
